import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../endpoints';
import {
  Order,
  OrderListResponse,
  OrderFilters,
  OrderSummary,
  OrderItem,
  OrderDetailResponse,
  OrderDetailData,
  OrderItemDetail,
  OrderStatus,
} from '../types/orderTypes';

/**
 * Get orders by filters for the manage orders listing page
 */
export const getOrdersByFilters = async (filters: OrderFilters): Promise<OrderListResponse> => {
  try {
    const queryParams: any = {};
    if (filters.pageNum) queryParams.pageNum = filters.pageNum;
    if (filters.pageSize) queryParams.pageSize = filters.pageSize;
    if (filters.status && filters.status !== 'All') {
      queryParams.status = filters.status;
    }
    if (filters.search) {
      queryParams.search = filters.search;
    }
    // Pass startAt and endAt as query parameters
    if (filters.startAt) {
      queryParams.startAt = filters.startAt;
    }
    if (filters.endAt) {
      queryParams.endAt = filters.endAt;
    }
    // Keep dateFrom/dateTo for backward compatibility if startAt/endAt are not provided
    if (!filters.startAt && filters.dateFrom) {
      queryParams.dateFrom = filters.dateFrom;
    }
    if (!filters.endAt && filters.dateTo) {
      queryParams.dateTo = filters.dateTo;
    }
    if (filters.period && filters.period !== 'All') {
      queryParams.period = filters.period;
    }

    const response = await secureApi.get<OrderListResponse>(SHOP_ENDPOINTS.ORDERS.MANAGE_LIST, {
      params: queryParams,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get order items list for the manage page (returns array directly)
 */
export const getOrderItems = async (filters: OrderFilters): Promise<{ items: OrderItem[], statusCounts: any, totalCount: number }> => {
  try {
    const response = await getOrdersByFilters(filters);
    
    // Map order items to ensure returnId is extracted from returnRequest if needed
    const mappedItems: OrderItem[] = (response.data.paginatedResults || []).map((orderItem: any) => {
      // Extract returnId from various possible locations in API response
      // Check order level first - check multiple possible field names
      let returnId = 
        orderItem.returnId || 
        orderItem.return_id || 
        orderItem.returnRequestId ||
        orderItem.return?.id ||
        orderItem.return?._id ||
        orderItem.returnId;
      
      // If returnRequest exists, try to get returnId from multiple locations
      if (orderItem.returnRequest) {
        // Check multiple possible locations for returnId within returnRequest
        const returnRequestId = 
          orderItem.returnRequest.returnId || 
          orderItem.returnRequest._id || 
          orderItem.returnRequest.id ||
          orderItem.returnRequest.returnRequestId ||
          orderItem.returnRequest.return?.id ||
          orderItem.returnRequest.return?._id;
        
        // Use returnId from returnRequest if found, otherwise keep order level returnId
        returnId = returnRequestId || returnId;
        
        // Also check if returnId might be at the same level as returnRequest (sibling fields)
        if (!returnId) {
          // Check if there's a return object at order level
          returnId = orderItem.return?.id || orderItem.return?._id;
          
          // Check if returnId might be a sibling of returnRequest
          // Some APIs might structure it as: { returnRequest: {...}, returnId: "..." }
          if (!returnId && orderItem.returnId) {
            returnId = orderItem.returnId;
          }
        }
        
        // Update returnRequest to include returnId if we found it
        if (returnId && !orderItem.returnRequest.returnId) {
          orderItem.returnRequest = {
            ...orderItem.returnRequest,
            returnId: returnId
          };
        }
      }
      
      // Return order item with returnId set at order level for easy access
      return {
        ...orderItem,
        returnId: returnId || orderItem.returnId,
        // Ensure returnRequest is preserved with returnId if we found it
        returnRequest: orderItem.returnRequest ? {
          ...orderItem.returnRequest,
          returnId: returnId || orderItem.returnRequest.returnId
        } : orderItem.returnRequest,
        // Include cancellationStatus if present in API response
        cancellationStatus: orderItem.cancellationStatus || orderItem.cancellation?.status || undefined
      };
    });
    
    return {
      items: mappedItems,
      statusCounts: response.data.statusCounts,
      totalCount: response.data.totalCount,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get returns list for the returns tab
 */
export const getReturns = async (pageNum: number = 1, pageSize: number = 10): Promise<{ items: OrderItem[], totalCount: number }> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.ORDERS.RETURNS, {
      params: { pageNum, pageSize },
    });

    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;

    // Map return items to OrderItem format
    const items: OrderItem[] = (apiData.paginatedResults || []).map((returnItem: any) => {
      // Calculate price breakdown from price
      const price = returnItem.price || 0;
      const gstRate = 18; // Default GST rate
      const taxableAmount = price / (1 + gstRate / 100);
      const gstAmount = price - taxableAmount;

      // Map return status to OrderStatus type
      // REQUESTED and REJECTED are return-specific statuses, map to 'RETURN'
      const returnStatus = returnItem.status || 'REQUESTED';
      const mappedStatus: OrderStatus = 
        returnStatus === 'REQUESTED' || returnStatus === 'REJECTED' 
          ? 'RETURN' 
          : (returnStatus as OrderStatus);

      const refundStatus = returnItem?.returnDetails?.refund?.status 
        || returnItem?.refund?.status 
        || returnItem?.refundStatus
        || null;

      return {
        customerName: returnItem.customerName || '',
        orderDate: returnItem.statusUpdatedAt || new Date().toISOString(), // Use statusUpdatedAt as orderDate
        orderId: returnItem.orderId || '',
        orderItemId: returnItem.orderItemId || '',
        price: price,
        priceBreakdown: {
          discountAmount: 0,
          grossAmount: price,
          gstAmount: gstAmount,
          gstRate: gstRate,
          taxableAmount: taxableAmount,
          totalAmount: price,
        },
        productName: returnItem.productName || '',
        status: mappedStatus,
        statusChangeDate: returnItem.statusUpdatedAt || null,
        returnId: returnItem._id || '', // Store return ID for navigation
        returnStatus: returnStatus, // Store original return status (REQUESTED, APPROVED, REFUNDED, REJECTED)
        refundStatus: refundStatus ? refundStatus.toUpperCase() : undefined, // Store refund status if available
      };
    });

    return {
      items,
      totalCount: apiData.totalCount || 0,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get return details by return ID
 */
export const getReturnDetails = async (returnId: string): Promise<any> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.ORDERS.GET_RETURN_DETAILS(returnId));
    
    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return apiData;
  } catch (error) {
    throw error;
  }
};

/**
 * Approve or reject return request
 */
export const approveRejectReturn = async (
  returnIds: string[], 
  status: 'APPROVED' | 'REJECTED', 
  note?: string
): Promise<void> => {
  try {
    const payload = {
      ids: returnIds,
      status: status,
      ...(note && { note }),
    };
    
    await secureApi.post(SHOP_ENDPOINTS.ORDERS.APPROVE_REJECT_RETURN, payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Initiate refund for a return
 */
export const initiateRefund = async (returnId: string): Promise<void> => {
  try {
    await secureApi.post(SHOP_ENDPOINTS.ORDERS.INITIATE_REFUND(returnId));
  } catch (error) {
    throw error;
  }
};

/**
 * Approve or reject cancellation request
 */
export const approveRejectCancellation = async (
  orderId: string,
  orderItemId: string,
  status: 'APPROVED' | 'REJECTED',
  reason?: string
): Promise<void> => {
  try {
    const payload = {
      status: status,
      ...(reason && { reason }),
    };
    
    await secureApi.post(SHOP_ENDPOINTS.ORDERS.APPROVE_REJECT_CANCELLATION(orderId, orderItemId), payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Initiate refund for a cancellation
 */
export const initiateCancellationRefund = async (orderId: string, orderItemId: string): Promise<void> => {
  try {
    await secureApi.post(SHOP_ENDPOINTS.ORDERS.INITIATE_CANCELLATION_REFUND(orderId, orderItemId));
  } catch (error) {
    throw error;
  }
};

/**
 * Legacy function for backward compatibility with existing components
 * Returns the old structure expected by SellerLocationDetail
 */
export const getOrdersByFiltersLegacy = async (filters: OrderFilters): Promise<{
  paginatedResults: Order[];
  totalCount: number;
  totalPages: number;
  page: number;
}> => {
  try {
    const queryParams: any = {};
    if (filters.pageNum) queryParams.pageNum = filters.pageNum;
    if (filters.sellerLocation) queryParams.sellerLocation = filters.sellerLocation;
    if (filters.status && filters.status !== 'All') {
      queryParams.status = filters.status;
    }
    if (filters.search) {
      queryParams.search = filters.search;
    }
    if (filters.dateFrom) {
      queryParams.dateFrom = filters.dateFrom;
    }
    if (filters.dateTo) {
      queryParams.dateTo = filters.dateTo;
    }

    const response = await secureApi.get<any>(SHOP_ENDPOINTS.ORDERS.MANAGE_LIST, {
      params: queryParams,
    });

    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      paginatedResults: (apiData.paginatedResults || []).map((orderItem: OrderItem) => ({
        _id: orderItem.orderItemId,
        orderId: orderItem.orderId,
        productName: orderItem.productName,
        orderedBy: orderItem.customerName,
        orderDate: orderItem.orderDate,
        price: orderItem.price,
        orderStatus: orderItem.status,
        // Map additional fields without overwriting
        customerName: orderItem.customerName,
        orderItemId: orderItem.orderItemId,
        status: orderItem.status,
        statusChangeDate: orderItem.statusChangeDate,
        priceBreakdown: orderItem.priceBreakdown,
      })),
      totalCount: apiData.totalCount || 0,
      totalPages: Math.ceil((apiData.totalCount || 0) / 10),
      page: filters.pageNum || 1,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get order by ID
 */
export const getOrderById = async (id: string): Promise<Order> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.ORDERS.GET_BY_ID(id));
    
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      ...apiData,
      _id: apiData._id,
      orderId: apiData.orderId || apiData._id,
      productName: apiData.productName || apiData.product?.name || '',
      orderedBy: apiData.orderedBy || apiData.customer || apiData.customerName || '',
      orderDate: apiData.orderDate || apiData.createdAt || '',
      price: apiData.price || apiData.total || 0,
      orderStatus: apiData.orderStatus || apiData.status || 'CONFIRMED',
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get order details by order ID and order item ID
 */
export const getOrderDetails = async (orderId: string, orderItemId: string): Promise<OrderDetailResponse> => {
  try {
    // Validate input parameters
    if (!orderId || !orderItemId) {
      throw new Error('Order ID and Order Item ID are required');
    }

    if (typeof orderId !== 'string' || typeof orderItemId !== 'string') {
      throw new Error('Order ID and Order Item ID must be strings');
    }

    const response = await secureApi.get<OrderDetailResponse>(
      SHOP_ENDPOINTS.ORDERS.GET_DETAILS(orderId, orderItemId)
    );

    // Validate response structure
    if (!response) {
      throw new Error('No response received from server');
    }

    // Handle different response formats (direct data or nested)
    let orderData: any;

    if ((response as any)?.data?.data) {
      // Response is wrapped (has response.data.data structure)
      orderData = (response as any).data.data;
    } else if ((response as any)?.data) {
      // Response has response.data structure  
      orderData = (response as any).data;
    } else {
      // Response is direct data
      orderData = response as any;
    }

    // Validate essential fields
    if (!orderData) {
      throw new Error('Invalid response format: missing order data');
    }

    // Transform API response structure to expected format
    // API can return either:
    // 1. orderItemInfo (single object) + otherItems (array) - new format
    // 2. orderItems (array) - old format
    // Expected: orderItems (array)
    let orderItems: OrderItemDetail[] = [];
    
    // Check if API returns new format (orderItemInfo + otherItems)
    if (orderData.orderItemInfo) {
      // Map orderItemInfo to OrderItemDetail format
      // Extract returnId from various possible locations (similar to getOrderItems)
      // Priority: returnRequestId (most common), then returnId, then returnRequest.returnId
      let mainItemReturnId = 
        orderData.orderItemInfo.returnRequestId || // Check returnRequestId first (most common in API)
        orderData.orderItemInfo.returnId || 
        orderData.orderItemInfo.return_id ||
        orderData.orderItemInfo.return?._id ||
        orderData.orderItemInfo.return?.id;
      
      // If returnRequest exists, try to get returnId from multiple locations
      if (orderData.orderItemInfo.returnRequest) {
        const returnRequestId = 
          orderData.orderItemInfo.returnRequest.returnId || 
          orderData.orderItemInfo.returnRequest._id || 
          orderData.orderItemInfo.returnRequest.id ||
          orderData.orderItemInfo.returnRequest.returnRequestId ||
          orderData.orderItemInfo.returnRequest.return?.id ||
          orderData.orderItemInfo.returnRequest.return?._id;
        
        mainItemReturnId = returnRequestId || mainItemReturnId;
      }
      
      // Also check if returnStatus exists (indicates item has been returned)
      const hasReturnStatus = orderData.orderItemInfo.returnStatus && 
        orderData.orderItemInfo.returnStatus !== null && 
        orderData.orderItemInfo.returnStatus !== undefined;
      
      // If returnRequestId exists but returnId wasn't set, use returnRequestId
      if (!mainItemReturnId && orderData.orderItemInfo.returnRequestId) {
        mainItemReturnId = orderData.orderItemInfo.returnRequestId;
      }
      
      const mainItem: OrderItemDetail = {
        _id: orderData.orderItemInfo._id || orderItemId || '', // Preserve order item ID
        color: orderData.orderItemInfo.color || '',
        expectedPickupDate: orderData.orderItemInfo.expectedPickupDate || null,
        image: orderData.orderItemInfo.image || '',
        isSameItem: true, // Mark main item
        orderStatusHistory: orderData.orderItemInfo.orderStatusHistory || [],
        pickupLocation: orderData.orderItemInfo.pickupLocation || '',
        price: orderData.orderItemInfo.price || 0,
        priceBreakdown: orderData.orderItemInfo.priceBreakdown || {
          discountAmount: 0,
          grossAmount: orderData.orderItemInfo.price || 0,
          gstAmount: 0,
          gstRate: 18,
          taxableAmount: 0,
          totalAmount: orderData.orderItemInfo.price || 0,
        },
        productName: orderData.orderItemInfo.productName || '',
        quantity: orderData.orderItemInfo.quantity || 1,
        seller: orderData.orderItemInfo.seller || '',
        sellerLocation: orderData.orderItemInfo.sellerLocation || {
          address: '',
          name: '',
        },
        returnRequest: orderData.orderItemInfo.returnRequest,
        returnId: mainItemReturnId || orderData.orderItemInfo.returnId || orderData.orderItemInfo.returnRequest?.returnId || orderData.orderItemInfo.returnRequestId,
        returnRequestId: orderData.orderItemInfo.returnRequestId, // Preserve returnRequestId separately
        returnStatus: orderData.orderItemInfo.returnStatus, // Preserve returnStatus to check if item has been returned
        cancellation: orderData.orderItemInfo.cancellation,
      };
      orderItems.push(mainItem);
      
      // Add other items if they exist
      if (orderData.otherItems && Array.isArray(orderData.otherItems)) {
        const otherItems: OrderItemDetail[] = orderData.otherItems.map((item: any) => {
          // Extract returnId from various possible locations (similar to getOrderItems)
          // Priority: returnRequestId (most common), then returnId, then returnRequest.returnId
          let returnId = 
            item.returnRequestId || // Check returnRequestId first (most common in API)
            item.returnId || 
            item.return_id ||
            item.return?._id ||
            item.return?.id;
          
          // If returnRequest exists, try to get returnId from multiple locations
          if (item.returnRequest) {
            const returnRequestId = 
              item.returnRequest.returnId || 
              item.returnRequest._id || 
              item.returnRequest.id ||
              item.returnRequest.returnRequestId ||
              item.returnRequest.return?.id ||
              item.returnRequest.return?._id;
            
            returnId = returnRequestId || returnId;
          }
          
          // If returnRequestId exists but returnId wasn't set, use returnRequestId
          if (!returnId && item.returnRequestId) {
            returnId = item.returnRequestId;
          }
          
          return {
            _id: item._id || item.orderItemId || '', // Preserve order item ID for navigation
            color: item.color || '',
            expectedPickupDate: item.expectedPickupDate || null,
            image: item.image || '',
            isSameItem: false,
            orderStatusHistory: item.orderStatusHistory || [],
            pickupLocation: item.pickupLocation || '',
            price: item.price || 0,
            priceBreakdown: item.priceBreakdown || {
              discountAmount: 0,
              grossAmount: item.price || 0,
              gstAmount: 0,
              gstRate: 18,
              taxableAmount: 0,
              totalAmount: item.price || 0,
            },
            productName: item.productName || '',
            quantity: item.quantity || 1,
            seller: item.seller || '',
            sellerLocation: item.sellerLocation || {
              address: '',
              name: '',
            },
            returnRequest: item.returnRequest,
            returnId: returnId || item.returnId || item.returnRequest?.returnId || item.returnRequestId,
            returnRequestId: item.returnRequestId, // Preserve returnRequestId separately
            returnStatus: item.returnStatus, // Preserve returnStatus to check if item has been returned
            cancellation: item.cancellation,
          };
        });
        orderItems = [...orderItems, ...otherItems];
    }
    } 
    // Fallback to old format (orderItems array)
    else if (orderData.orderItems && Array.isArray(orderData.orderItems)) {
      orderItems = orderData.orderItems;
    }

    // Validate that we have at least one order item
    if (orderItems.length === 0) {
      throw new Error('No order items found for this order');
    }

    // Validate that at least one item has the required fields
    const validItems = orderItems.filter((item: OrderItemDetail) => 
      item.productName && 
      item.orderStatusHistory && 
      Array.isArray(item.orderStatusHistory)
    );

    if (validItems.length === 0) {
      throw new Error('No valid order items with complete data found');
    }

    // Construct the final order data with transformed orderItems
    // Preserve orderItemInfo if it exists (needed for cancellation checks)
    const transformedOrderData: OrderDetailData & { orderItemInfo?: any; otherItems?: any[] } = {
      contact: orderData.contact || {
        email: '',
        name: '',
        phone: '',
      },
      feeBreakup: orderData.feeBreakup || {
        discountAmount: 0,
        grossAmount: 0,
        gstAmount: 0,
        gstRate: 18,
        taxableAmount: 0,
        totalAmount: 0,
      },
      id: orderData.id || orderData.orderId || '',
      orderDate: orderData.orderDate || '',
      orderId: orderData.orderId || '',
      orderItems: orderItems,
      paymentMethod: orderData.paymentMethod || {
        method: '',
      },
      // Preserve original orderItemInfo for cancellation/return checks
      orderItemInfo: orderData.orderItemInfo,
      otherItems: orderData.otherItems,
    };

    // Return properly formatted response
    return {
      data: transformedOrderData,
      message: (response as any)?.message || 'Order details fetched successfully',
      status: (response as any)?.status || 'success',
      statusCode: (response as any)?.statusCode || 200
    } as OrderDetailResponse;
  } catch (error: any) {
    // Re-throw with more context if it's a network/API error
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      
      if (status === 404) {
        throw new Error(`Order not found: ${message}`);
      } else if (status === 403) {
        throw new Error(`Access denied: ${message}`);
      } else if (status >= 500) {
        throw new Error(`Server error (${status}): ${message}`);
      } else {
        throw new Error(`API error (${status}): ${message}`);
      }
    }
    
    // Re-throw validation or other errors as-is
    throw error;
  }
};

/**
 * Cancel order by order ID and order item ID (admin)
 */
export const cancelOrder = async (orderId: string, orderItemId: string, reason: string): Promise<any> => {
  try {
    // Validate input parameters
    if (!orderId || !orderItemId) {
      throw new Error('Order ID and Order Item ID are required');
    }

    if (typeof orderId !== 'string' || typeof orderItemId !== 'string') {
      throw new Error('Order ID and Order Item ID must be strings');
    }

    if (!reason || !reason.trim()) {
      throw new Error('Cancellation reason is required');
    }

    const payload = {
      reason: reason.trim()
    };

    const response = await secureApi.post(
      SHOP_ENDPOINTS.ORDERS.CANCEL_ADMIN(orderId, orderItemId),
      payload
    );

    // Handle different response formats
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;

    return apiData;
  } catch (error: any) {
    // Re-throw with more context if it's a network/API error
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      
      if (status === 404) {
        throw new Error(`Order not found: ${message}`);
      } else if (status === 403) {
        throw new Error(`Access denied: ${message}`);
      } else if (status >= 500) {
        throw new Error(`Server error (${status}): ${message}`);
      } else {
        throw new Error(`API error (${status}): ${message}`);
      }
    }
    
    // Re-throw validation or other errors as-is
    throw error;
  }
};

/**
 * Mark multiple orders as ready for pickup
 */
export const markOrdersReadyForPickup = async (orderItemIds: string[]): Promise<void> => {
  try {
    const payload = {
      orderItems: orderItemIds
    };
    
    await secureApi.post('/api/v1/shop/fulfillment/ready-for-pickup', payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Mark multiple orders as picked up/delivered
 */
export const markOrdersPickedUp = async (orderItemIds: string[]): Promise<void> => {
  try {
    const payload = {
      orderItems: orderItemIds
    };
    
    await secureApi.post('/api/v1/shop/fulfillment/picked-up', payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Calculate order summary from orders list
 */
export const calculateOrderSummary = (orders: Order[]): OrderSummary => {
  return {
    confirmed: orders.filter(o => o.orderStatus === 'CONFIRMED').length,
    readyForPickup: orders.filter(o => o.orderStatus === 'READY_FOR_PICKUP').length,
    delivered: orders.filter(o => o.orderStatus === 'DELIVERED').length,
    returned: orders.filter(o => o.orderStatus === 'RETURN').length,
    replacement: orders.filter(o => o.orderStatus === 'REPLACEMENT').length,
  };
};
